/** Document freezing (§3.6): a document's payload is complete at creation and never
 *  references live data. Issue makes it immutable; editing after issue = a revision. */
import type {
  Client, DocType, DocumentRecord, FrozenDoc, FrozenLine, Job, PriceItem, Settings, TakeoffLine, InventoryItem,
} from '../core/types';
import { computeBid, resolveLines, type ResolvedLine } from './compute';

const PREFIX: Record<DocType, string> = {
  proposal: 'P', progress_update: 'U', change_order: 'CO', walkthrough: 'W',
};

/** B-1002 + existing docs → B-1002-P2 etc. Counts every doc of the type, superseded included. */
export function nextDocNumber(type: DocType, bidNumber: string, existing: DocumentRecord[]): string {
  const n = existing.filter((d) => d.type === type).length + 1;
  return `${bidNumber}-${PREFIX[type]}${n}`;
}

export interface FreezeInputs {
  job: Job;
  /** The client record this job belongs to. Optional only so that jobs created
   *  before clients existed still freeze; see clientNameFor. */
  client?: Client | null;
  lines: TakeoffLine[];
  prices: PriceItem[];
  inventory: InventoryItem[];
  settings: Settings;
  existingDocs: DocumentRecord[];
  todayIso: string; // YYYY-MM-DD
}

/**
 * The name that gets printed on a document a client signs.
 *
 * DEFECT, found 2026-07-27: this used to be the ONLY source — parse the job's
 * free-text notes for a line beginning with the exact string "Client: " and slice
 * eight characters off it. Probed: "client:", "Client -", "Client:" with no
 * space, and a single leading space all produced a blank name on the proposal.
 * Issued documents are immutable, so the only correction was to revise and
 * reissue after the client had already seen it.
 *
 * The client record is now the source. The notes parse survives only to keep
 * documents readable for jobs created before clients existed, and it is the last
 * resort rather than the first.
 */
export function clientNameFor(i: Pick<FreezeInputs, 'job' | 'client'>): string {
  const fromRecord = (i.client?.company || i.client?.name || '').trim();
  if (fromRecord) return fromRecord;
  const legacy = (i.job.notes ?? '').split('\n').find((l) => /^\s*client\s*[:\-]/i.test(l));
  return legacy ? legacy.replace(/^\s*client\s*[:\-]\s*/i, '').trim() : '';
}

function baseDoc(i: FreezeInputs): Omit<FrozenDoc, 'lines' | 'math'> {
  return {
    company: { ...i.settings.company },
    clientName: clientNameFor(i),
    siteAddress: i.job.siteAddress,
    parcel: i.job.parcel ? { apn: i.job.parcel.apn, lotSqFt: i.job.parcel.lotSqFt } : null,
    bidNumber: i.job.bidNumber,
    issuedDate: i.todayIso,
    validityDays: i.settings.defaults.validityDays,
    termsText: i.settings.termsText,
  };
}

export function freezeProposal(i: FreezeInputs, narrative: string): FrozenDoc {
  const { lines, math } = computeBid(resolveLines(i.lines, i.prices), i.job.fin);
  return { ...baseDoc(i), lines, math, narrative: narrative || undefined };
}

export function freezeWalkthrough(i: FreezeInputs): FrozenDoc {
  const { lines, math } = computeBid(resolveLines(i.lines, i.prices), i.job.fin);
  return {
    ...baseDoc(i), lines, math,
    inventory: i.inventory.map((iv) => ({
      cat: iv.cat, species: iv.species, qty: iv.qty, cond: iv.cond,
      notes: iv.notes, verified: iv.verification.verified,
    })),
  };
}

export function freezeProgress(i: FreezeInputs, body: { summary: string; next: string; notes: string }): FrozenDoc {
  const { lines, math } = computeBid(resolveLines(i.lines, i.prices), i.job.fin);
  return { ...baseDoc(i), lines, math, body: { ...body } };
}

/**
 * Change order = the audited delta between the last issued proposal and the takeoff now.
 * Changed lines appear as an explicit credit line plus a revised line, so every amount
 * is still qty × rate — the arithmetic stays visible (§4.3).
 */
export function changeOrderLines(base: FrozenLine[], current: ResolvedLine[]): FrozenLine[] {
  const key = (name: string, label?: string) => `${name}||${label ?? ''}`;
  const baseBy = new Map(base.map((l) => [key(l.name, l.label), l]));
  const curBy = new Map<string, ResolvedLine>();
  for (const c of current) curBy.set(key(c.name, c.label), c);

  const out: FrozenLine[] = [];
  for (const [k, c] of curBy) {
    const b = baseBy.get(k);
    const amountCents = Math.round(c.qty * c.rateCents);
    if (!b) {
      out.push({ name: c.name, label: c.label ? `Added — ${c.label}` : 'Added', qty: c.qty, unit: c.unit, rateCents: c.rateCents, amountCents, source: c.source, verified: c.verified });
    } else if (b.qty !== c.qty || b.rateCents !== c.rateCents) {
      out.push({ name: b.name, label: `Credit — as proposed${b.label ? ` (${b.label})` : ''}`, qty: -b.qty, unit: b.unit, rateCents: b.rateCents, amountCents: -b.amountCents, source: c.source, verified: c.verified });
      out.push({ name: c.name, label: `Revised${c.label ? ` — ${c.label}` : ''}`, qty: c.qty, unit: c.unit, rateCents: c.rateCents, amountCents, source: c.source, verified: c.verified });
    }
  }
  for (const [k, b] of baseBy) {
    if (!curBy.has(k)) {
      out.push({ name: b.name, label: `Removed — as proposed${b.label ? ` (${b.label})` : ''}`, qty: -b.qty, unit: b.unit, rateCents: b.rateCents, amountCents: -b.amountCents, source: 'manual', verified: false });
    }
  }
  return out;
}

export function freezeChangeOrder(i: FreezeInputs, baseProposal: DocumentRecord, note: string): FrozenDoc {
  const current = resolveLines(i.lines, i.prices);
  const deltaLines = changeOrderLines(baseProposal.payload.lines, current);
  const subtotalCents = deltaLines.reduce((s, l) => s + l.amountCents, 0);
  const markupCents = Math.round((subtotalCents * i.job.fin.markupPct) / 100);
  const taxBase = subtotalCents + markupCents;
  const taxCents = Math.round((taxBase * i.job.fin.taxPct) / 100);
  return {
    ...baseDoc(i),
    lines: deltaLines,
    math: {
      subtotalCents, markupPct: i.job.fin.markupPct, markupCents,
      adjustCents: 0, taxPct: i.job.fin.taxPct, taxCents,
      totalCents: taxBase + taxCents,
      depositPct: 0, depositCents: 0, // deposits are not re-collected on change orders
    },
    body: { note, baseNumber: baseProposal.number },
  };
}
